import dynamic from 'next/dynamic';
import { useCallback, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import useUser from '@alltrails/context/hooks/useUser';
import { useLanguageRegionCode } from '@alltrails/core/hooks/useLanguageRegionCode';
import { wrapUrlSafe } from '@alltrails/core/utils/wrapUrlSafe';
import useIsMobileSizedScreen from '@alltrails/denali/hooks/useIsMobileSizedScreen';
import type { AuthedEvent, EventMapType } from '../types';

const CreateEventModal = dynamic(() => import('../components/CreateEventModal'), { ssr: false });
const ShareEventModal = dynamic(() => import('../components/ShareEventModal'), { ssr: false });
const SignUpModal = dynamic(() => import('@alltrails/user-auth/components/SignUpModal'), { ssr: false });

type useEventCreationArgs = {
  mapId: number;
  mapType: EventMapType;
};

const useCreateEventCta = ({ mapId, mapType }: useEventCreationArgs) => {
  const intl = useIntl();
  const user = useUser();
  const isMobile = useIsMobileSizedScreen();
  const languageRegionCode = useLanguageRegionCode();

  const [createdEvent, setCreatedEvent] = useState<AuthedEvent>();
  const [isCreateEventModalOpen, setIsCreateEventModalOpen] = useState(false);
  const [isShareEventModalOpen, setIsShareEventModalOpen] = useState(false);
  const [isSignupModalOpen, setIsSignupModalOpen] = useState(false);

  const onClick = useCallback(() => {
    if (user) {
      setIsCreateEventModalOpen(true);
    } else {
      setIsSignupModalOpen(true);
    }
  }, [user]);

  const onEventCreation = useCallback(
    (event: AuthedEvent) => {
      setIsCreateEventModalOpen(false);
      const eventSlug = wrapUrlSafe(`/event/${event.id}`, languageRegionCode);
      if (isMobile) {
        try {
          navigator.share({ title: event.title, text: event.title, url: window.location.origin + eventSlug });
        } catch {
          setCreatedEvent(event);
          setIsShareEventModalOpen(true);
        }
      } else {
        window.location.assign(eventSlug);
      }
    },
    [isMobile, languageRegionCode]
  );

  const modals = useMemo(
    () => (
      <>
        {isSignupModalOpen && (
          <SignUpModal
            isOpen={isSignupModalOpen}
            closeModal={() => setIsSignupModalOpen(false)}
            deeplinkData={mapType === 'T' ? { pageId: mapId, pageType: 'trail' } : undefined}
            onSuccess={() => {
              setIsSignupModalOpen(false);
              setIsCreateEventModalOpen(true);
            }}
            trigger="create invite"
            upsellTrigger="create invite"
          />
        )}
        {isCreateEventModalOpen && (
          <CreateEventModal
            mapId={mapId}
            mapType={mapType}
            onCloseRequest={() => setIsCreateEventModalOpen(false)}
            onSuccess={onEventCreation}
            // Override the submit button text for mobile users since the share flow is triggered instead
            submitButtonText={isMobile ? intl.formatMessage({ defaultMessage: 'Invite friends' }) : undefined}
          />
        )}
        {isShareEventModalOpen && createdEvent && (
          <ShareEventModal
            isOpen
            onClose={() => {
              setIsShareEventModalOpen(false);
              setCreatedEvent(undefined);
            }}
            event={createdEvent}
          />
        )}
      </>
    ),
    [isSignupModalOpen, isCreateEventModalOpen, mapId, mapType, onEventCreation, isMobile, isShareEventModalOpen, createdEvent, intl]
  );

  return useMemo(() => ({ modals, onClick }), [modals, onClick]);
};

export default useCreateEventCta;
